<script setup lang="ts">
import { inject, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { createUserStore } from '@/stores/user-store';
import { createExternalConnectionsStore } from '@/stores/external-connections-store';
import { LOGIN_REDIRECT_KEY } from '@/definitions';
import { callKey, dayjsKey } from '@/keys';
import { userManager } from '@/composables/oidcUserManager';
import { BooleanResponse } from '@/models';
import { isFxaAuth, isOidcAuth } from '@/composables/authSchemes';

const route = useRoute();
const router = useRouter();

// component constants
const call = inject(callKey);
const user = createUserStore(call);
const externalConnectionsStore = createExternalConnectionsStore(call);
const dj = inject(dayjsKey);

onMounted(async () => {
  // Retrieve and remove temp login redirect location
  const redirectTo = window.sessionStorage?.getItem(LOGIN_REDIRECT_KEY);
  window.sessionStorage?.removeItem(LOGIN_REDIRECT_KEY);
  // Remove any ftue steps on new login
  window.localStorage?.removeItem('tba/ftue');

  if (isOidcAuth) {
    // Stored in an internal store
    const userData = await userManager.signinCallback(window.location.href);
    const timezone = userData?.profile?.zoneinfo ?? dj.tz.guess();

    const { error, data }: BooleanResponse = await call('oidc/token')
      .post({
        access_token: userData.access_token,
        timezone: timezone,
      })
      .json();

    if (error.value) {
      console.error('Err', data.value);
      return;
    }
  } else if (!isFxaAuth) {
    await router.push(redirectTo ?? '/');
    return;
  }

  if (isOidcAuth) {
    await user.login('true', null);
  } else {
    await user.login(route.params.token as string, null);
  }

  // Run health checks on external connections in the background
  externalConnectionsStore.checkStatus();

  // TODO: Commenting this out for now as there are some intermittent issues in Sentry
  // https://thunderbird.sentry.io/issues/7463150573/events/27fa49bce44244efb470683d241a35df/?project=4508965616680960&referrer=event-or-group-header

  // Fetch waffle feature flags and store them on the user
  // const { error: featureFlagsError } = await user.updateFeatureFlags();
  // if (featureFlagsError) {
  //   console.error('Could not retrieve waffle flags', featureFlagsError);
  // }

  // If we don't have a redirectTo or it's to logout then push to dashboard!
  if (!redirectTo || redirectTo === '/logout') {
    await router.push('/dashboard');
    return;
  }

  await router.push(redirectTo);
});
</script>

<template>
  <div>
    <!-- Intentionally left blank -->
  </div>
</template>
